<template>
  <div class="px-2 pt-4">
    <template v-if="loadingAllRecords">
      <v-layout>
        <v-flex md4 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="skeleton-block skeleton-title mb-4" />
            <div class="skeleton-block skeleton-line mb-3" />
            <div class="skeleton-block skeleton-progress mb-4" />
            <div class="skeleton-block skeleton-line mb-3" />
            <div class="skeleton-block skeleton-line mb-4" />
            <div class="skeleton-chip-row">
              <div class="skeleton-block skeleton-chip" />
              <div class="skeleton-block skeleton-chip" />
              <div class="skeleton-block skeleton-chip" />
            </div>
          </v-card>
        </v-flex>
        <v-flex md8 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="skeleton-block skeleton-title" />
              <div class="skeleton-block skeleton-toggle" />
            </div>
            <div class="d-flex align-center mb-4">
              <div class="skeleton-block skeleton-legend mr-2" />
              <div class="skeleton-block skeleton-legend mr-2" />
              <div class="skeleton-block skeleton-legend" />
            </div>
            <div class="skeleton-block skeleton-chart" />
          </v-card>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex md4 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="skeleton-block skeleton-stat-icon mb-3" />
            <div class="skeleton-block skeleton-stat-value mb-2" />
            <div class="skeleton-block skeleton-line" />
          </v-card>
        </v-flex>
        <v-flex md4 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="skeleton-block skeleton-stat-icon mb-3" />
            <div class="skeleton-block skeleton-stat-value mb-2" />
            <div class="skeleton-block skeleton-line" />
          </v-card>
        </v-flex>
        <v-flex md4 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="skeleton-block skeleton-stat-icon mb-3" />
            <div class="skeleton-block skeleton-stat-value mb-2" />
            <div class="skeleton-block skeleton-line" />
          </v-card>
        </v-flex>
      </v-layout>
    </template>
    <template v-else>
    <!-- Stats Tiles -->
    <v-layout>
        <v-flex md4 class="px-2">
          <v-card class="summary-tile pa-4" outlined>
            <div class="summary-tile__header">
              <div>
                <p class="summary-tile__eyebrow mb-1">Summary</p>
                <h4 class="summary-tile__title mb-0">Draft Pipeline Snapshot</h4>
              </div>
              <v-icon color="#1565C0">mdi-chart-box-outline</v-icon>
            </div>
            <div class="summary-tile__section">
              <div class="summary-tile__row">
                <span class="summary-tile__label">Completion vs Draft</span>
                <span class="summary-tile__value">{{ totalSubmitted }} / {{ totalDraftSubmissions }}</span>
              </div>
              <v-progress-linear
                :value="completionRate"
                color="#4CAF50"
                background-color="#E3F2FD"
                height="8"
                rounded
              />
              <p class="summary-tile__hint mb-0">{{ completionRate }}% completed from the current pipeline</p>
            </div>
            <div class="summary-tile__section">
              <div class="summary-tile__row">
                <span class="summary-tile__label">Pending Actions</span>
                <span class="summary-tile__value">{{ pendingActions }}</span>
              </div>
              <p class="summary-tile__hint mb-0">Draft and registered opportunities not yet submitted</p>
            </div>
            <div class="summary-tile__section mb-0">
              <div class="summary-tile__row">
                <span class="summary-tile__label">New Drafts This Week</span>
                <span class="summary-tile__value">{{ latestDraftIds.length }}</span>
              </div>
              <div class="summary-tile__chips">
                <v-chip
                  v-for="draftId in latestDraftIds"
                  :key="draftId"
                  class="summary-tile__chip"
                  color="#FFF3E0"
                  small
                  text-color="#E65100"
                  @click="selectDraftId(draftId)"
                >
                  {{ draftId }}
                </v-chip>
              </div>
            </div>
          </v-card>
        </v-flex>
        <v-flex md8 class="px-2">
            <material-chart-card
            :data="filteredChartData.data"
            :options="filteredChartData.options"
            :responsive-options="filteredChartData.responsiveOptions"
            color="#1565C0"
            type="Bar"
            >
            <div class="d-flex align-center mt-2 ml-2 mr-2">
                <h4 class="card-title font-weight-light">
                Draft Opportunities by Status
                </h4>
                <div style="text-align: right;">
                <v-btn-toggle v-model="selectedFilter">
                <v-btn small value="quarter">Quarter</v-btn>
                <v-btn small value="month">Month</v-btn>
                <v-btn small value="week">Week</v-btn>
                </v-btn-toggle>
                </div>
            </div>
            <div class="d-flex align-center ml-2 mt-1 mb-1">
                <span class="legend-dot legend-draft mr-1" />
                <span class="caption grey--text font-weight-light mr-3">Draft</span>
                <span class="legend-dot legend-registered mr-1" />
                <span class="caption grey--text font-weight-light mr-3">Registered</span>
                <span class="legend-dot legend-submitted mr-1" />
                <span class="caption grey--text font-weight-light">Submitted</span>
            </div>
            <template v-slot:actions>
                <v-icon class="mr-1" small>mdi-clock-outline</v-icon>
                <span class="caption grey--text font-weight-light">{{defaultTime}}</span>
            </template>
            </material-chart-card>
        </v-flex>
    </v-layout>
    <!-- Bar Chart -->
    <v-layout>
      <v-flex md4 class="px-2">
            <material-stats-card
                :value="totalDraftSubmissions"
                color="orange"
                icon="mdi-file-outline"
                title="Total Draft Submissions"
                sub-icon="mdi-calendar"
                :sub-text="getLatestStatusText('draft')"
            />
        </v-flex>
        <v-flex md4 class="px-2">
            <material-stats-card
                :value="totalRegistered"
                color="blue"
                icon="mdi-file-outline"
                title="Total Registered to Galaxy"
                sub-icon="mdi-calendar"
                :sub-text="getLatestStatusText('registered')"
            />
        </v-flex>
        <v-flex md4 class="px-2">
            <material-stats-card
                :value="totalSubmitted"
                color="green"
                icon="mdi-check-circle-outline"
                title="Total Submitted to ACE"
                sub-icon="mdi-calendar"
                :sub-text="getLatestStatusText('submitted')"
            />
        </v-flex>
    </v-layout>
    </template>
  </div>
</template>

<script>

import { mapState } from 'vuex'
export default {
  data: () => ({

    selectedFilter: 'quarter',
    chartBaseOptions: {
      axisX: { showGrid: false },
      low: 0,
      chartPadding: { top: 0, right: 5, bottom: 0, left: 0 },
      height: '200px'
    },
    responsiveOptions: [
      ['screen and (max-width: 640px)', {
        seriesBarDistance: 5,
        axisX: {
          labelInterpolationFnc: function (value) {
            return value[0]
          }
        }
      }]
    ]
  }),
    computed: {
        ...mapState('admin/draftOpportunities', ['records', 'allRecords', 'loadingAllRecords']),
        
        totalDraftSubmissions () {
            return this.allRecords.filter(r => r.status === 'draft' && r.is_deleted === 0).length
        },
        totalRegistered () {
            return this.allRecords.filter(r => r.status === 'registered' && r.is_deleted === 0).length
        },
        totalSubmitted () { 
            return this.allRecords.filter(r => r.status === 'submitted' && r.is_deleted === 0).length
        },
        latestDraftIds () {
            return this.allRecords
                .filter(r => r.status === 'draft' && r.is_deleted === 0)
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5)
                .map(r => r.opportunity_id)
        },
        defaultTime () {
            switch (this.selectedFilter) {
                case 'quarter': return 'Last 4 quarters'
                case 'month': return 'Last 4 months'
                case 'week': return 'Last 4 weeks'
            }
        },
        completionRate () {
            const totalOpportunities = this.totalDraftSubmissions + this.totalRegistered + this.totalSubmitted

            if (!totalOpportunities) return 0
                return Number(((this.totalSubmitted / totalOpportunities) * 100).toFixed(1))
            },
        pendingActions () {
            return Number(this.totalDraftSubmissions) + Number(this.totalRegistered)
        },
        chartSeriesByFilter () {
          return {
            quarter: this.buildStatusSeries('quarter'),
            month: this.buildStatusSeries('month'),
            week: this.buildStatusSeries('week')
          }
        },
        filteredChartData () {
          const { labels, series, high } = this.chartSeriesByFilter[this.selectedFilter]
            return {
                data: { labels, series },
                options: { ...this.chartBaseOptions, high },
                responsiveOptions: this.responsiveOptions
            }
        }
    },
    methods: {
        selectDraftId (draftId) {
          this.$emit('select-draft-id', draftId)
        },
        buildStatusSeries (period) {
          const periods = this.getRecentPeriods(period)
          const statusKeys = ['draft', 'registered', 'submitted']
          const counters = periods.reduce((accumulator, entry) => {
            accumulator[entry.key] = { draft: 0, registered: 0, submitted: 0 }
            return accumulator
          }, {})

          this.allRecords.forEach(record => {
            const status = String(record.status || '').toLowerCase()
            const createdAt = new Date(record.created_at)

            if (!statusKeys.includes(status) || Number.isNaN(createdAt.getTime())) return

            const periodKey = this.getPeriodKey(createdAt, period)

            if (counters[periodKey]) {
              counters[periodKey][status] += 1
            }
          })

          const series = statusKeys.map(status => periods.map(entry => counters[entry.key][status]))
          const highestValue = Math.max(0, ...series.flat())

          return {
            labels: periods.map(entry => entry.label),
            series,
            high: highestValue ? Math.ceil(highestValue * 1.2) : 5
          }
        },
        getRecentPeriods (period) {
          const current = new Date()
          const periods = []

          for (let offset = 3; offset >= 0; offset -= 1) {
            if (period === 'quarter') {
              const date = new Date(current.getFullYear(), current.getMonth() - (offset * 3), 1)
              const quarter = Math.floor(date.getMonth() / 3) + 1
              periods.push({
                key: `${date.getFullYear()}-Q${quarter}`,
                label: `Q${quarter}`
              })
            }

            if (period === 'month') {
              const date = new Date(current.getFullYear(), current.getMonth() - offset, 1)
              periods.push({
                key: `${date.getFullYear()}-${date.getMonth()}`,
                label: date.toLocaleString('en-US', { month: 'short' })
              })
            }

            if (period === 'week') {
              const date = new Date(current)
              date.setDate(current.getDate() - (offset * 7))
              const weekStart = this.getWeekStart(date)
              periods.push({
                key: `${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`,
                label: this.formatWeekRange(weekStart)
              })
            }
          }

          return periods
        },
        getPeriodKey (date, period) {
          if (period === 'quarter') {
            return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`
          }

          if (period === 'month') {
            return `${date.getFullYear()}-${date.getMonth()}`
          }

          const weekStart = this.getWeekStart(date)
          return `${weekStart.getFullYear()}-${weekStart.getMonth()}-${weekStart.getDate()}`
        },
        getWeekStart (date) {
          const weekStart = new Date(date)
          const day = weekStart.getDay()
          const diff = day === 0 ? -6 : 1 - day
          weekStart.setHours(0, 0, 0, 0)
          weekStart.setDate(weekStart.getDate() + diff)
          return weekStart
        },
        formatWeekRange (weekStart) {
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekStart.getDate() + 6)

          const startMonth = weekStart.toLocaleString('en-US', { month: 'short' })
          const endMonth = weekEnd.toLocaleString('en-US', { month: 'short' })
          const startDay = weekStart.getDate()
          const endDay = weekEnd.getDate()

          if (weekStart.getMonth() === weekEnd.getMonth() && weekStart.getFullYear() === weekEnd.getFullYear()) {
            return `${startMonth} ${startDay}-${endDay}`
          }

          return `${startMonth} ${startDay}-${endMonth} ${endDay}`
        },
        getLatestStatusText (status) {
          const latestRecord = this.allRecords
            .filter(record => String(record.status || '').toLowerCase() === status)
            .sort((firstRecord, secondRecord) => {
              const firstDate = new Date(firstRecord.updated_at || firstRecord.created_at)
              const secondDate = new Date(secondRecord.updated_at || secondRecord.created_at)

              return secondDate - firstDate
            })[0]

          if (!latestRecord) return 'No records yet'

          const latestDate = new Date(latestRecord.updated_at || latestRecord.created_at)
          const formattedDate = latestDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          })

          return `Latest: ${formattedDate}`
        }
      },
    mounted() {
        this.$store.dispatch('admin/draftOpportunities/getAllRecords')
    },
}
</script>

<style scoped>
.summary-tile {
  border: 1px solid #dbe7f3;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(21, 101, 192, 0.08);
}

.skeleton-block {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: #e8eef5;
}

.skeleton-block::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, rgba(232, 238, 245, 0) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(232, 238, 245, 0) 100%);
  animation: skeleton-shimmer 1.4s infinite;
}

.skeleton-title {
  width: 55%;
  height: 24px;
}

.skeleton-line {
  width: 100%;
  height: 14px;
}

.skeleton-progress {
  width: 100%;
  height: 8px;
}

.skeleton-chip-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.skeleton-chip {
  width: 84px;
  height: 24px;
}

.skeleton-toggle {
  width: 180px;
  height: 32px;
}

.skeleton-legend {
  width: 72px;
  height: 16px;
}

.skeleton-chart {
  width: 100%;
  height: 260px;
}

.skeleton-stat-icon {
  width: 56px;
  height: 56px;
}

.skeleton-stat-value {
  width: 45%;
  height: 22px;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.summary-tile__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.summary-tile__eyebrow {
  color: #1565c0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.summary-tile__title {
  color: #1f2937;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
}

.summary-tile__section {
  margin-bottom: 18px;
}

.summary-tile__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.summary-tile__label {
  color: #374151;
  font-size: 14px;
  font-weight: 500;
}

.summary-tile__value {
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}

.summary-tile__hint {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}

.summary-tile__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-tile__chip {
  margin: 0;
  cursor: pointer;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
}
.legend-draft      { background-color: #FF9800; }
.legend-registered { background-color: #2196F3; }
.legend-submitted  { background-color: #4CAF50; }
</style>

<style>
.v-card--material-chart .v-card--material__header .ct-series-a .ct-bar { 
  stroke: #FF9800 !important; 
  fill: #FF9800 !important; 
}
.v-card--material-chart .v-card--material__header .ct-series-b .ct-bar { 
  stroke: #2196F3 !important; 
  fill: #2196F3 !important; 
}
.v-card--material-chart .v-card--material__header .ct-series-c .ct-bar { 
  stroke: #4CAF50 !important; 
  fill: #4CAF50 !important; 
}
</style>
